import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { LineChart as LineChartIcon, Loader2 } from 'lucide-react';
import { api, errorMessage } from '../../lib/api';
import { fmtDate, fmtEur, fmtPrice } from '../../lib/format';
import { cn } from '../../lib/utils';

const RANGES = [
  { days: 90, label: '3 m' },
  { days: 180, label: '6 m' },
  { days: 365, label: '1 y' },
  { days: 730, label: '2 y' },
];

const COLORS = {
  shop: '#1f2937',
  market: '#2563eb',
  breakeven: '#dc2626',
  cost: '#9ca3af',
  rrp: '#a78bfa',
};

const shortDate = (iso) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

function Stat({ label, value, sub, tone }) {
  return (
    <div className="min-w-0 rounded-lg border bg-card px-2.5 py-2">
      <div className="text-[10.5px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn('truncate text-sm font-medium tabular-nums', tone === 'bad' && 'text-red-600', tone === 'good' && 'text-emerald-700')}>{value}</div>
      {sub && <div className="truncate text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-lg border bg-background px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-medium">{fmtDate(label)}</div>
      {row.shop != null && (
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">Our price</span>
          <span className="tabular-nums font-medium">{fmtPrice(row.shop)}</span>
        </div>
      )}
      {row.market != null && (
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">Cheapest competitor</span>
          <span className="tabular-nums font-medium" style={{ color: COLORS.market }}>
            {fmtPrice(row.market)}
          </span>
        </div>
      )}
      {row.seller && <div className="mt-0.5 text-right text-[11px] text-muted-foreground">{row.seller}</div>}
    </div>
  );
}

export default function PriceHistory({ odooId }) {
  const [days, setDays] = useState(365);
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['price-history', String(odooId), days],
    queryFn: () => api.priceHistory(odooId, days),
    enabled: !!odooId,
    staleTime: 10 * 60 * 1000,
  });

  const series = useMemo(() => {
    if (!data) return [];
    const byDate = new Map();
    for (const p of data.shop || []) byDate.set(p.date, { date: p.date, shop: p.price });
    for (const p of data.market_min || []) byDate.set(p.date, { ...(byDate.get(p.date) || { date: p.date }), market: p.price, seller: p.seller });
    return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
  }, [data]);

  const ref = data?.reference || {};
  const s = data?.summary || {};
  const values = series.flatMap((r) => [r.shop, r.market]).filter((v) => v != null);
  const refs = [ref.breakeven_price, ref.cost_incl_vat, ref.rrp].filter((v) => v != null);
  const lo = Math.min(...values, ...refs);
  const hi = Math.max(...values, ...refs);
  const pad = Math.max(5, (hi - lo) * 0.08);
  const step = data?.source === 'odoo_base_price';

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold">
          <LineChartIcon className="h-4 w-4 text-muted-foreground" /> Price history
          {isFetching && !isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
        </h3>
        <div className="flex rounded-md border p-0.5">
          {RANGES.map((r) => (
            <button
              key={r.days}
              type="button"
              onClick={() => setDays(r.days)}
              className={cn('rounded px-2 py-0.5 text-xs', days === r.days ? 'bg-accent font-medium text-foreground' : 'text-muted-foreground hover:text-foreground')}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <div className="h-[260px] animate-pulse rounded-lg bg-muted/60" />}
      {error && <p className="text-sm text-destructive">{errorMessage(error)}</p>}
      {data && !series.length && (
        <p className="rounded-lg border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
          No price history recorded for this item in the selected period.
        </p>
      )}

      {data && series.length > 0 && (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat
              label="Our price now"
              value={fmtPrice(s.current_price)}
              sub={s.change_pct != null ? `${s.change_pct > 0 ? '+' : ''}${s.change_pct}% vs ${fmtDate(series[0].date)}` : undefined}
            />
            <Stat label="Odoo markdowns" value={String(s.markdowns ?? 0)} sub="base price lowered in period" />
            <Stat label="Cheapest competitor" value={s.market_min_now != null ? fmtPrice(s.market_min_now) : '—'} sub={s.market_seller_now || 'not seen in the scrape'} />
            <Stat
              label="Us vs market"
              value={s.vs_market_pct != null ? `${s.vs_market_pct > 0 ? '+' : ''}${s.vs_market_pct}%` : '—'}
              sub={s.vs_market_pct == null ? 'no competitor price' : s.vs_market_pct > 0 ? 'we are more expensive' : 'we are cheaper'}
              tone={s.vs_market_pct == null ? undefined : s.vs_market_pct > 0 ? 'bad' : 'good'}
            />
          </div>

          <div className="rounded-lg border bg-card p-2 pr-3">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={series} margin={{ top: 10, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={shortDate}
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={40}
                />
                <YAxis
                  domain={[Math.max(0, Math.floor(lo - pad)), Math.ceil(hi + pad)]}
                  tickFormatter={(v) => fmtEur(v)}
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                  width={64}
                />
                <Tooltip content={<ChartTooltip />} />
                {ref.rrp != null && <ReferenceLine y={ref.rrp} stroke={COLORS.rrp} strokeDasharray="2 4" label={{ value: 'RRP', position: 'insideTopRight', fontSize: 10, fill: COLORS.rrp }} />}
                {ref.breakeven_price != null && (
                  <ReferenceLine y={ref.breakeven_price} stroke={COLORS.breakeven} strokeDasharray="5 4" label={{ value: 'Break-even', position: 'insideTopLeft', fontSize: 10, fill: COLORS.breakeven }} />
                )}
                {ref.cost_incl_vat != null && (
                  <ReferenceLine y={ref.cost_incl_vat} stroke={COLORS.cost} strokeDasharray="2 3" label={{ value: 'Cost incl. VAT', position: 'insideBottomLeft', fontSize: 10, fill: COLORS.cost }} />
                )}
                <Line type="monotone" dataKey="market" stroke={COLORS.market} strokeWidth={1.5} strokeDasharray="4 3" dot={false} connectNulls isAnimationActive={false} />
                <Line type={step ? 'stepAfter' : 'monotone'} dataKey="shop" stroke={COLORS.shop} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            <Legend color={COLORS.shop} label={step ? 'Our base price (Odoo, incl. VAT)' : 'Our shop price (lamptwist.com, BE, daily)'} />
            <Legend color={COLORS.market} dashed label="Cheapest competitor (EU, EUR)" />
            <Legend color={COLORS.breakeven} dashed label={`Break-even ${fmtPrice(ref.breakeven_price)}`} />
            <Legend color={COLORS.cost} dashed label={`Cost incl. VAT ${fmtPrice(ref.cost_incl_vat)}`} />
            {ref.rrp != null && <Legend color={COLORS.rrp} dashed label={`RRP ${fmtPrice(ref.rrp)}`} />}
          </div>

          {(data.base_price_changes?.length > 0 || data.cost_changes?.length > 0) && (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                Odoo changes in this period ({data.base_price_changes.length} price, {data.cost_changes.length} cost)
              </summary>
              <ul className="mt-2 space-y-1 tabular-nums">
                {[
                  ...data.base_price_changes.map((c) => ({ ...c, kind: 'Price (incl. VAT)' })),
                  ...data.cost_changes.map((c) => ({ ...c, kind: 'Cost (ex-VAT)' })),
                ]
                  .sort((a, b) => b.date.localeCompare(a.date))
                  .map((c, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="w-24 shrink-0 text-muted-foreground">{fmtDate(c.date)}</span>
                      <span className="w-32 shrink-0">{c.kind}</span>
                      <span>
                        {fmtPrice(c.from)} → <span className={cn('font-medium', c.to < c.from ? 'text-emerald-700' : 'text-red-600')}>{fmtPrice(c.to)}</span>
                      </span>
                    </li>
                  ))}
              </ul>
            </details>
          )}
        </>
      )}
    </section>
  );
}

function Legend({ color, label, dashed }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <svg width="18" height="6" aria-hidden="true">
        <line x1="0" y1="3" x2="18" y2="3" stroke={color} strokeWidth="2" strokeDasharray={dashed ? '4 3' : undefined} />
      </svg>
      {label}
    </span>
  );
}
